/**
 * Every module this build of the app knows how to show — the mirror of
 * `backend/crates/server/src/modules.rs`. The account's licence and the user's permissions decide
 * what appears. Order here is the order of nav groups contributed by plugins.
 */
import { type ModulePlugin, registerPlugins } from "@keel/core";
import accounting from "@keel/module-accounting";
import assets from "@keel/module-assets";
import assistant from "@keel/module-assistant";
import finance from "@keel/module-finance";
import hr from "@keel/module-hr";
import integrations from "@keel/module-integrations";
import inventory from "@keel/module-inventory";
import loans from "@keel/module-loans";
import manufacturing from "@keel/module-manufacturing";
import masterdata from "@keel/module-masterdata";
import payroll from "@keel/module-payroll";
import platform from "@keel/module-platform";
import purchasing from "@keel/module-purchasing";
import sales from "@keel/module-sales";
import warehouse from "@keel/module-warehouse";

export const PLUGINS: ModulePlugin[] = [
  sales,
  purchasing,
  inventory,
  warehouse,
  masterdata,
  finance,
  accounting,
  hr,
  payroll,
  manufacturing,
  assets,
  loans,
  integrations,
  assistant,
  platform,
];

registerPlugins(PLUGINS);

export const activePlugins = (modules: string[] | undefined) =>
  PLUGINS.filter((p) => (modules ?? []).includes(p.key));

/**
 * The sidebar's sections, in order, with the icon each shows in the rail.
 *
 * A section is a nav `group` that one or more plugins contribute items to, so this list is the one
 * place that decides what the first level of navigation looks like. A group no plugin here names
 * still appears — after these and before Platform, with a neutral icon — so a new module is never
 * invisible just because this list was not updated.
 */
export const SECTIONS: { group: string; icon: string }[] = [
  { group: "Sales", icon: "cart" },
  { group: "Purchasing", icon: "factory" },
  { group: "Inventory", icon: "boxes" },
  { group: "Warehouse", icon: "warehouse" },
  { group: "Finance", icon: "wallet" },
  { group: "Accounting", icon: "ledger" },
  { group: "Manufacturing", icon: "cog" },
  { group: "Fixed assets", icon: "building" },
  { group: "Loans", icon: "cash" },
  { group: "People", icon: "users" },
  { group: "Platform", icon: "settings" },
];

const RANKS = new Map(SECTIONS.map((s, i) => [s.group, i]));
/** Where an unlisted group sorts: with the rest, but never past Platform. */
const UNLISTED = SECTIONS.length - 1.5;

export const sectionRank = (group: string) => RANKS.get(group) ?? UNLISTED;
export const sectionIcon = (group: string) => SECTIONS.find((s) => s.group === group)?.icon ?? "grid";
